import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { LeftArrowIcon } from '@components/shared/icon/LeftArrowIcon';
import { RightArrowIcon } from '@components/shared/icon/RightArrowIcon';

import { cn } from '@utils/index';

import { W2_SELECT_TID } from '../constants/testids';

/**
 * The two-panel selection shell — a THIN, props-driven copy of the B2C
 * `OnboardingFormStepLayout` visual (maroon left panel + notch + STEP N/M +
 * title/body + pill PREV/NEXT, white middle card) with NONE of its stores or
 * on-mount API calls. Mounting the real B2C layout on `/select-plans` would fire
 * B2C requests under a W2 JWT and render nothing without B2C form state
 * (plan §1.3), so the load-bearing class strings are copied here instead and the
 * two flows stay visually aligned while staying fully decoupled.
 *
 * Renders standalone with zero providers (no store reads) — the phase components
 * drive it entirely by props.
 */
export interface W2SelectionStepLayoutProps {
  /** 1-based bar position (drives "STEP n / m"). */
  step: number;
  total: number;
  /**
   * Suppress the "step n / m" line in the left panel. Used by the pre-questionnaire
   * PHQ gate screen (the waiver), which is not one of the 3 questionnaire
   * sections, so a misleading "STEP 1/2" would be worse than none. Default false →
   * every existing selection screen renders the counter exactly as before.
   */
  hideStepLabel?: boolean;
  /** Left-panel title. */
  title: string;
  /** Testid for the title element (e.g. the plans category heading). */
  titleTestId?: string;
  /** Left-panel body copy (paragraphs / rich nodes). */
  body?: React.ReactNode;

  onPrev?: () => void;
  prevLabel?: string;
  prevTestId?: string;
  onNext?: () => void;
  nextLabel?: string;
  nextTestId?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  /** Hide the NEXT pill (e.g. the agreement keeps FINISH inside the card). */
  hideNext?: boolean;

  /**
   * Drop the white card's inner padding so its child can bleed to the card's
   * edges — the B2C `onlyImage` welcome treatment, whose image block is a bare
   * `bg-card w-full rounded-2xl` (`OnboardingFormStepLayout:537`). Welcome
   * screens only; every other screen keeps the padded card.
   */
  fullBleed?: boolean;
  /**
   * Mirror B2C's welcome orientation — image card LEFT, maroon panel RIGHT
   * (`OnboardingFormStepLayout:518` `2xl:flex-row-reverse`). Welcome screens
   * only (captain decision D4); the rest of the flow stays panel-left.
   */
  reversed?: boolean;

  /** Testid for the outer two-panel wrapper. */
  rootTestId?: string;
  /** The middle white-card content. */
  children: React.ReactNode;
}

const usePanelVariants = () => {
  const reduce = useReducedMotion();
  return {
    initial: {
      opacity: 0,
      y: reduce ? 0 : 20,
      filter: reduce ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
    },
  };
};

interface NavPillsProps {
  onPrev?: () => void;
  prevLabel?: string;
  prevTestId?: string;
  onNext?: () => void;
  nextLabel?: string;
  nextTestId?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  hideNext?: boolean;
  variant: 'panel' | 'bottom';
}

/**
 * PREV/NEXT pills — the `NavigationButtons` visual (outline Previous, solid Next
 * with arrow) stripped of the B2C `useForm`/`useFormControls` plumbing. Only the
 * desktop `panel` instance carries testids so a single `getByTestId` resolves at
 * the e2e desktop viewport (the `bottom` mobile instance is testid-free).
 */
const NavPills: React.FC<NavPillsProps> = ({
  onPrev,
  prevLabel = 'Previous',
  prevTestId,
  onNext,
  nextLabel = 'Next',
  nextTestId,
  nextDisabled,
  nextLoading,
  hideNext,
  variant,
}) => {
  const showNext = !!onNext && !hideNext;
  const isPanel = variant === 'panel';

  const prevClass = isPanel
    ? 'group flex flex-1 items-center justify-center gap-2 rounded-full border border-white h-13 px-6 text-[16px] font-bold uppercase text-white transition-colors hover:bg-white hover:text-primary disabled:pointer-events-none disabled:opacity-50'
    : 'group flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-primary bg-white h-14 px-6 text-[16px] font-bold uppercase text-primary transition-colors disabled:pointer-events-none disabled:opacity-50';
  const nextClass = isPanel
    ? 'group flex flex-1 items-center justify-center gap-2 rounded-full bg-white h-13 px-6 text-[16px] font-bold uppercase text-primary transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-40'
    : 'group flex flex-1 items-center justify-center gap-2 rounded-full bg-primary h-14 px-6 text-[16px] font-bold uppercase text-white transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="mt-2 flex w-full gap-4">
      {onPrev && (
        <button
          type="button"
          data-testid={isPanel ? prevTestId : undefined}
          onClick={onPrev}
          className={prevClass}
        >
          <LeftArrowIcon
            color={isPanel ? 'var(--color-card)' : 'var(--color-primary)'}
            className="group-hover:opacity-80"
          />
          <span className="flex-1 text-center">{prevLabel}</span>
        </button>
      )}
      {showNext && (
        <button
          type="button"
          data-testid={isPanel ? nextTestId : undefined}
          onClick={onNext}
          disabled={nextDisabled || nextLoading}
          className={cn(nextClass, !onPrev && 'flex-none w-full')}
        >
          <span className="flex-1 text-center">
            {nextLoading ? 'Saving…' : nextLabel}
          </span>
          <RightArrowIcon
            color={isPanel ? 'var(--color-primary)' : 'var(--color-card)'}
          />
        </button>
      )}
    </div>
  );
};

export const W2SelectionStepLayout: React.FC<W2SelectionStepLayoutProps> = ({
  step,
  total,
  hideStepLabel,
  title,
  titleTestId,
  body,
  onPrev,
  prevLabel,
  prevTestId,
  onNext,
  nextLabel,
  nextTestId,
  nextDisabled,
  nextLoading,
  hideNext,
  fullBleed,
  reversed,
  rootTestId,
  children,
}) => {
  const variants = usePanelVariants();
  const hasNav = !!onPrev || (!!onNext && !hideNext);

  return (
    <div
      data-testid={rootTestId}
      className={cn(
        'flex h-full w-full flex-1 flex-col gap-4',
        reversed ? '2xl:flex-row-reverse' : '2xl:flex-row'
      )}
    >
      {/* Left maroon panel — copied class strings from OnboardingFormStepLayout. */}
      <motion.div
        variants={variants}
        initial="initial"
        animate="animate"
        data-testid={W2_SELECT_TID.shellPanel}
        className={cn(
          'relative flex w-full flex-col gap-4 rounded-3xl bg-primary p-5 text-white lg:px-7.5 lg:pt-10 lg:pb-8',
          'max-lg:min-w-fit max-xl:min-h-102.5 max-lg:min-h-0 lg:justify-between 2xl:max-w-[34.83%]'
        )}
      >
        <div className="pointer-events-none absolute top-25 -right-1 h-8 w-8 rotate-45 bg-primary select-none max-2xl:hidden" />
        <section className="relative flex flex-col gap-6 max-md:gap-2 lg:pr-1.5">
          {!hideStepLabel && (
            <p className="text-[19px] leading-6 tracking-[30%] uppercase max-md:text-[14px]">
              step {step} / {total}
            </p>
          )}
          <h2
            data-testid={titleTestId}
            className="text-2xl leading-7 font-bold max-md:tracking-[1px] lg:text-4xl lg:leading-10 3xl:text-[40px]"
          >
            {title}
          </h2>
          {body && (
            <div className="flex flex-col gap-4 text-base max-md:mt-2.5 max-md:leading-normal">
              {body}
            </div>
          )}
        </section>
        {hasNav && (
          <div className="mt-auto flex max-2xl:hidden">
            <NavPills
              variant="panel"
              onPrev={onPrev}
              prevLabel={prevLabel}
              prevTestId={prevTestId}
              onNext={onNext}
              nextLabel={nextLabel}
              nextTestId={nextTestId}
              nextDisabled={nextDisabled}
              nextLoading={nextLoading}
              hideNext={hideNext}
            />
          </div>
        )}
      </motion.div>

      {/* Middle white card. */}
      <motion.div
        variants={variants}
        initial="initial"
        animate="animate"
        className={cn(
          'relative box-border w-full rounded-2xl bg-card max-2xl:flex-1',
          fullBleed && 'overflow-hidden'
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col',
            fullBleed ? 'h-full' : 'p-6 md:p-8 max-sm:p-5'
          )}
        >
          {children}
        </div>
      </motion.div>

      {/* Mobile nav (sub-2xl) — mirrors BottomNavigationButtons; testid-free. */}
      {hasNav && (
        <div className="flex 2xl:hidden">
          <NavPills
            variant="bottom"
            onPrev={onPrev}
            prevLabel={prevLabel}
            onNext={onNext}
            nextLabel={nextLabel}
            nextDisabled={nextDisabled}
            nextLoading={nextLoading}
            hideNext={hideNext}
          />
        </div>
      )}
    </div>
  );
};
